import { useState } from 'react'
import type { ReactElement } from 'react'
import { AppRoot } from '../components/AppRoot'
import { signOut } from '../lib/auth'
import { useSession } from '../lib/session-context'
import { Button } from '../ui'

/**
 * Shown at `/` when `decideGate` returns `'NO_PROFILE'` — a live session with no `profiles` row
 * (`handle_new_user` failed, the fetch errored, or RLS legitimately returned zero rows; see
 * `gate.ts`'s own doc comment). Per that comment and CLAUDE.md's "never show an unexplained
 * warning" rule, this must say something concrete and must never be confused with
 * `SIGNED_OUT` — the user *did* sign in successfully; the app just couldn't find or load their
 * profile row.
 *
 * Retry re-fetches the profile via `useSession()`'s `refresh()` (Task 10) without disturbing the
 * session. Sign-out is the escape hatch if retrying doesn't resolve it. Both report their own
 * failure rather than discarding it: this screen exists *because* something already went wrong,
 * so a Retry that silently does nothing is the worst possible outcome here.
 *
 * Wrapped in exactly one `<AppRoot>`, like every other top-level screen (`JoinScreen`'s
 * not-signed-in branch explains why exactly one, never zero, never two). Without it nothing
 * renders inside `.sl-root`, `base.css` never sets `--ink`, and under a dark OS preference this
 * screen is black text on the near-black `--paper` that `app.css` paints onto `body`.
 */
export function NoProfileNotice(): ReactElement {
  const { refresh } = useSession()

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // `refresh()` is typed `Promise<void>` and has no error channel of its own — `SessionProvider`
  // collapses a failed `fetchProfile` into `profile === null`, the same way `gate.ts` does. So
  // the only failure this can catch is a thrown one (a network fault escaping supabase-js), and
  // catching it is what stops that becoming a silent unhandled rejection with a dead-looking
  // button. A retry that resolves with the profile still missing simply leaves this screen up.
  async function handleRetry() {
    setBusy(true)
    setError(null)
    try {
      await refresh()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
    setBusy(false)
  }

  async function handleSignOut() {
    setBusy(true)
    setError(null)
    const { error: signOutError } = await signOut()
    // No navigation needed on success — SessionProvider's onAuthStateChange subscription picks
    // up the cleared session and AppRoutes redirects to /sign-in on its own next render.
    if (signOutError) {
      setError(signOutError.message)
      setBusy(false)
    }
  }

  return (
    <AppRoot>
      <div className="sl-stack">
        <div>
          <h1 className="sl-heading">We couldn&apos;t load your profile.</h1>
          <p className="sl-hint">
            You&apos;re signed in, but the app couldn&apos;t find your account details. Try again,
            or sign out and back in.
          </p>
        </div>
        {error ? (
          <p className="sl-hint" role="alert">
            {error}
          </p>
        ) : null}
        <Button variant="primary" block disabled={busy} onClick={() => void handleRetry()}>
          Retry
        </Button>
        <Button variant="secondary" block disabled={busy} onClick={() => void handleSignOut()}>
          Sign out
        </Button>
      </div>
    </AppRoot>
  )
}
